
import { Swiper, SwiperSlide } from 'swiper/react';

import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, IconButton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Title, useDataProvider } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import pxLogo from './assets/images/logo-pontuax.svg';
import AttendantGoalOfferCard from './components/AttendantGoalOfferCard';

// Vídeos de lançamento, feitos no pontuax-motion (reels/05-painel-home) e publicados no S3.
// Ficam no código porque cada peça tem vídeo + pôster e é versionada lá; as imagens avulsas
// continuam vindo de /home-carousel-active (tela "Imagens da Home").
const MOTION_URL = 'https://s3.pontuax.com.br/marketing/motion';
const LAUNCH_VIDEOS = [
  { video: `${MOTION_URL}/PontuaX_home_cadastro_sorteio_web.mp4`, poster: `${MOTION_URL}/PontuaX_home_cadastro_sorteio.jpg`, title: 'Sorteio: cadastro no app vale número da sorte' },
  { video: `${MOTION_URL}/PontuaX_home_codigo_divulgacao_web.mp4`, poster: `${MOTION_URL}/PontuaX_home_codigo_divulgacao.jpg`, title: 'Clube de Vantagens: código de divulgação que mede cada campanha' },
  { video: `${MOTION_URL}/PontuaX_home_pix_antecipado_web.mp4`, poster: `${MOTION_URL}/PontuaX_home_pix_antecipado.jpg`, title: 'Clube de Vantagens: desconto de combustível vendido por Pix' },
];

// Quem pede menos movimento no sistema vê o pôster com os controles, sem autoplay.
const REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const CarouselImage = ({ url, alt, onLoad }) => {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: 'auto',
      aspectRatio: 'auto',
    }}>
      <img
        src={url}
        alt={alt}
        onLoad={onLoad}
        style={{
          width: '100%',
          height: 'auto',
          borderRadius: 4,
          display: 'block',
          objectFit: 'scale-down',
        }}
        loading="lazy"
      />
    </div>
  );
};

const CarouselVideo = ({ slide, active, muted, onToggleMuted, onEnded }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || REDUCED_MOTION) return;
    if (!active) {
      video.pause();
      return;
    }
    video.currentTime = 0;
    // Com som, o navegador pode recusar o play sem gesto do usuário: cai para mudo.
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => { });
    });
  }, [active]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <video
        ref={ref}
        src={slide.video}
        poster={slide.poster}
        aria-label={slide.title}
        muted={muted}
        playsInline
        controls={REDUCED_MOTION}
        preload={active ? 'auto' : 'none'}
        onEnded={onEnded}
        onError={active ? onEnded : undefined}
        style={{
          width: '100%',
          aspectRatio: '2400 / 1104',
          borderRadius: 4,
          display: 'block',
        }}
      />
      {!REDUCED_MOTION &&
        <IconButton
          onClick={onToggleMuted}
          aria-label={muted ? 'Ativar som' : 'Desativar som'}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            color: '#fff',
            backgroundColor: '#2323238c',
            '&:hover': { backgroundColor: '#232323cc' },
          }}
        >
          {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
        </IconButton>
      }
    </div>
  );
};

const ImageCarousel = ({ slides }) => {
  const swiperRef = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);

  // Slide de vídeo fica na tela até o vídeo acabar; o timer de 5 s vale só para imagem.
  const videoIsPlaying = !REDUCED_MOTION && Boolean(slides[activeIndex]?.video);
  useEffect(() => {
    if (!swiperInstance || swiperInstance.destroyed) return;
    if (videoIsPlaying) swiperInstance.autoplay.stop();
    else swiperInstance.autoplay.start();
  }, [swiperInstance, videoIsPlaying]);

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      pb: 8,
    }}>
      <Box sx={{
        width: '100%',
        alignSelf: 'flex-start'
      }}>
        <Box
          component="img"
          src={pxLogo}
          alt="PontuaX Logo"
          sx={{
            alignSelf: 'left',
            width: { xs: 300, md: 350 },
            height: 'auto',
            mb: 3,
            mt: 4,
          }}
        />
      </Box>

      <Box sx={{
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'center',
      }}>
        <Box sx={{
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
        }}>
          <Swiper
            ref={swiperRef}
            onSwiper={setSwiperInstance}
            slidesPerView={1}
            spaceBetween={30}
            pagination={{ clickable: true }}
            loop
            autoHeight
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            navigation
            modules={[Pagination, Navigation, Autoplay]}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.realIndex);
              swiper.updateAutoHeight();
            }}
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={index}>
                {slide.video ?
                  <CarouselVideo
                    slide={slide}
                    active={index === activeIndex}
                    muted={muted}
                    onToggleMuted={() => setMuted((m) => !m)}
                    onEnded={() => swiperInstance?.slideNext()}
                  />
                  :
                  <CarouselImage
                    url={slide.image}
                    alt={`Slide ${index + 1}`}
                    onLoad={() => swiperInstance?.updateAutoHeight()}
                  />
                }
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>
    </Box>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [images, setImages] = useState(null); // null = ainda carregando

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const manualSlug = params.get('manual');
    if (manualSlug) {
      const section = params.get('section');
      navigate(`/manual/${manualSlug}${section ? `#${section}` : ''}`, { replace: true });
    }
  }, []);

  useEffect(() => {
    dataProvider.getList('home-carousel-active', {
      filter: {},
      sort: { field: 'displayOrder', order: 'ASC' },
      pagination: { page: 1, perPage: 50 },
    })
      .then(({ data }) => setImages(data.map((i) => i.imageUrl)))
      .catch(() => setImages([]));
  }, [dataProvider]);

  return (<>
    <Title title={"Painel Administrador"} />
    {/* width 0 + minWidth 100%: o RaLayout-root é fit-content e, sem isso, o slide de 1200 px
        empurra a página para além da tela (cortava a direita do carrossel em notebook) */}
    <div style={{ width: 0, minWidth: '100%', margin: '0 auto' }}>
      <AttendantGoalOfferCard />
      {/* monta só depois da resposta: o Swiper em loop não lida bem com slides chegando depois */}
      {images && <ImageCarousel slides={[...LAUNCH_VIDEOS, ...images.map((image) => ({ image }))]} />}
    </div>
  </>);
};

export default Home;